import { useState, useEffect, useCallback, useRef } from "react";
import { PdfAnnotationSubtype } from "@embedpdf/react-pdf-viewer";
import useAxios from "./useAxios";
import Constants from "../Constants";

const STRAY_SUBTYPES = [PdfAnnotationSubtype.SQUARE, PdfAnnotationSubtype.FREETEXT];

// Strips stray Square/FreeText annotations from a page; returns true once any were removed
const removeStrays = (cap) => {
    const strays = cap
        .getAnnotations()
        .filter((a) => STRAY_SUBTYPES.includes(a.object.type));

    if (strays.length === 0) return false;

    cap.deleteAnnotations(
        strays.map((a) => ({ pageIndex: a.object.pageIndex, id: a.object.id })),
    );
    return true;
};

const useRenderPdf = ({ s3Path, onSave }) => {
    const { postData } = useAxios();
    const [signedUrl, setSignedUrl] = useState("");
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const registryRef = useRef(null);

    useEffect(() => {
        if (!s3Path) {
            setSignedUrl("");
            return;
        }

        const resolveSignedUrl = async () => {
            setIsReady(false);
            setError("");
            const response = await postData(Constants.ApiUrl.utils.getSignedUrl, { path: s3Path });
            const url = response?.data?.signedUrl;

            if (url) {
                setSignedUrl(url);
            } else {
                setError("Unable to load the PDF document");
            }
        };

        resolveSignedUrl();
    }, [postData]);

    // Some source pdfs carry stray Square annotations (empty white boxes with
    // selection handles) plus a red FreeText privacy line at the bottom of the
    // page. Strip both once annotations load; form-field widgets are untouched.
    const onReadyHandler = useCallback((registry) => {
        registryRef.current = registry;

        // onReady only means the viewer mounted, so gate Save on the document actually opening
        const documentManager = registry.getPlugin("document-manager");
        if (documentManager) {
            const docCap = documentManager.provides();
            if (docCap.getActiveDocument()) {
                setIsReady(true);
            } else {
                const unsubscribeDoc = docCap.onDocumentOpened(() => {
                    setIsReady(true);
                    unsubscribeDoc();
                });
            }
        }

        const annotation = registry.getPlugin("annotation");
        if (!annotation) return;

        const cap = annotation.provides();

        // Already loaded, nothing left to listen for
        if (removeStrays(cap)) return;

        // Otherwise wait for them to stream in, then stop churning on later changes
        const unsubscribe = cap.onStateChange(() => removeStrays(cap) && unsubscribe());
    }, []);

    // Export the current document (filled fields / annotations applied) as
    // binary and hand it to the caller's onSave
    const handleSave = useCallback(async () => {
        const registry = registryRef.current;
        if (!registry) return;

        const exportPlugin = registry.getPlugin("export");
        if (!exportPlugin) return;

        setIsSaving(true);
        try {
            const buffer = await exportPlugin.provides().saveAsCopy().toPromise();
            const fileName = "FROG_PROPOUND.pdf";
            const file = new File([buffer], fileName, { type: "application/pdf" });
            await onSave?.({ file: [file] });
        } catch (err) {
            setError("Failed to save the PDF document");
            console.log('Failed to save the PDF document', err)
        } finally {
            setIsSaving(false);
        }
    }, [onSave]);

    return { signedUrl, error, isSaving, isReady, onReadyHandler, handleSave };
};

export default useRenderPdf;
